import * as yup from 'yup';
import { CASE_TYPE } from './enum';
import { DefaultMultiPlaintiffName } from './constant';
import { generateRandomIds } from './helper';

const sourceSchema = yup.object({
    s3UrlPath: yup.string().strict(),
    name: yup.string().strict()
}).required();

const adjusterSchema = yup.object({
    name: yup.string().strict(),
    phoneNumber: yup.string().strict(),
    email: yup.string().strict(),
    address: yup.string().strict(),
    city: yup.string().strict(),
    state: yup.string().strict(),
    zip: yup.string().strict()
}).required();

const medicalRecordSchema = yup.object({
    providerName: yup.string().strict(),
    source: yup.array(sourceSchema).required()
}).required();

const medicalBillRecordSchema = yup.object({
    totalBillAmount: yup.string().strict(),
    date: yup.string().strict(),
    medicalProviderName: yup.string().strict()
}).required();

const documentWithResultSchema = yup.object({
    s3UrlPath: yup.string().strict(),
    name: yup.string().strict(),
    result: yup.object({
        response: yup.string().strict(),
        exhibitPagesS3UrlPaths: yup.array(yup.string().strict()).required()
    })
}).required();

const futureExpenseSchema = yup.object({
    s3UrlPath: yup.string().strict(),
    name: yup.string().strict(),
    result: yup.object({
        response: yup.string().strict(),
        exhibitPagesS3UrlPaths: yup.array(yup.string().strict()).required(),
        expenseBillsData: yup.string().strict()
    })
}).required();

const liabilityItemSchema = yup.object({
    text: yup.string().strict(),
    defendants: yup.array(yup.string().strict()).required(),
    result: yup.object({
        response: yup.string().strict()
    })
}).required();

const liabilityDocumentSchema = yup.object({
    s3UrlPath: yup.string(),
    name: yup.string(),
    defendants: yup.array(yup.string()).required(),
    result: yup.object({
        response: yup.string(),
        exhibitPagesS3UrlPaths: yup.array(yup.string()).required()
    })
}).required();

const defendantSchema = yup.object({
    defendantId: yup.string(),
    personalInfo: yup.object({
        name: yup.string()
    }).required(),

    insurance: yup.object({
        insuranceName: yup.string(),
        claimNumber: yup.string(),
        adjuster: adjusterSchema.required()
    }).required(),

    result: yup.object({
        policeReportResponse: yup.string(),
        witnessStatementsResponse: yup.string(),
        incidentReportResponse: yup.string(),
        expertSafetyReportResponse: yup.string(),
        factsOfIncidentLLMResponse: yup.string(),
        dangerousConditionLLMResponse: yup.string(),
        actualConstructedNoticeLLMResponse: yup.string()
    })
}).required();

const caseSchema = yup.object({
    caseInfo: yup.object({
        caseName: yup.string().strict(),
        caseType: yup.string().strict(),
        dateOfIncident: yup.string().strict(),
        stateOfIncident: yup.string().strict(),
        isIncludeExhibit: yup.boolean().required(),
        isForceReset: yup.boolean().required(),
        isSingleLiabilitySectionForAllDefendants: yup.boolean().default(false)
    }).required(),

    clients: yup.array(yup.object({
        clientId: yup.string().strict(),
        personalInfo: yup.object({
            clientName: yup.string().strict(),
            phoneNumber: yup.string().strict(),
            email: yup.string().strict(),
            ssn: yup.string().strict(),
            gender: yup.string().strict(),
            age: yup.number().required()
        }).required(),

        insurance: yup.object({
            insuranceName: yup.string(),
            claimNumber: yup.string(),
            adjuster: adjusterSchema.required()
        }).required(),

        medicalProviders: yup.object({
            postAccident: yup.object({
                medicalRecords: yup.array(medicalRecordSchema).required(),
                result: yup.object({
                    visitDates: yup.array(yup.string()).required(),
                    executiveSummary: yup.string(),
                    medicalRecordsParagraphs: yup.string(),
                    simplifiedTreatmentParagraphs: yup.string(),
                    textExtractions: yup.array(yup.string()),
                    medicalBillRecords: yup.array(medicalBillRecordSchema).required(),
                    medicalRecordsExhibitDirectoryPath: yup.array(yup.string()).required(),
                    medicalRecords: yup.array(yup.mixed().required()).required()
                })
            }).required(),

            preAccident: yup.object({
                medicalRecords: yup.array(medicalRecordSchema).required(),
                result: yup.object({
                    visitDates: yup.array(yup.string()).required(),
                    medicalRecordsParagraphs: yup.string(),
                    medicalRecordsExhibitDirectoryPath: yup.array(yup.string()).required(),
                    textExtractions: yup.array(yup.string()),
                    medicalRecords: yup.array(yup.mixed().required()).required()
                })
            }).required()
        }).required(),

        damage: yup.object({
            workHoursMissed: yup.number().required(),
            hourlyIncomeRate: yup.number().required(),
            typeofWork: yup.string(),
            lossOfEarning: yup.array(documentWithResultSchema).required(),
            futureMedicalExpense: yup.array(futureExpenseSchema).required()
        }).required(),

        painAndSuffering: yup.object({
            monthlyamount: yup.string(),
            annualamount: yup.string(),
            descriptionText: yup.string(),
            bodilyInjuriesImageFiles: yup.array(yup.object({
                s3UrlPath: yup.string(),
                name: yup.string(),
                isSelected: yup.boolean().required()
            }).required()).required(),
            selectedPainPoints: yup.array(yup.string()).required(),
            result: yup.object()
        }).required()
    }).required()).required(),

    defendants: yup.array(defendantSchema).required(),

    liability: yup.object({
        howincidentOccured: yup.array(liabilityItemSchema),
        dangerousConditions: yup.array(liabilityItemSchema),
        factsRegardingNotice: yup.array(liabilityItemSchema),
        witnessStatements: yup.array(liabilityDocumentSchema),
        incidentReports: yup.array(liabilityDocumentSchema),

        accidentSceneFiles: yup.array(yup.object({
            s3UrlPath: yup.string().required(),
            name: yup.string().required(),
            defendants: yup.array(yup.string().required()).required()
        }).required()),

        policeReportData: yup.array(yup.object({
            s3UrlPath: yup.string().required(),
            name: yup.string().required(),
            text: yup.string(),
            defendants: yup.array(yup.string()),
            result: yup.object({
                response: yup.string().required(),
                exhibitPagesS3UrlPaths: yup.array(yup.string().required()).required()
            })
        }).required()),

        expertSafetyReport: yup.array(liabilityDocumentSchema)
    }).required(),

    result: yup.object(),
    resultProgress: yup.object({
        policeReport_Progress: yup.string(),
        medicalRecords_progress: yup.string(),
        preMedicalRecords_progress: yup.string(),
        medicalBills_progress: yup.string(),
        demandLetter_progress: yup.string(),
        caseLoadingPercentage: yup.number().required(),
        runningJobId: yup.string()
    }),

    s3UniqueId: yup.string(),
    isCaseEdited: yup.boolean(),
    createdOn: yup.string(),
    updatedOn: yup.string(),
    isMedicalBillProcessing: yup.boolean(),
    isCaseNeedtoShow: yup.boolean(),
    isCaseGeneratedSccessfuly: yup.boolean(),
    istimetaken: yup.string(),
    processTime: yup.string(),
    userId: yup.string(),
    invoiceFilePath: yup.string(),
    docxFiles: yup.object(),
    isDraftCase: yup.boolean().required(),
    isPreProcessRecordLoading: yup.boolean().default(false),
    processCount: yup.number().default(0),
    teamIds: yup.array(yup.string().required()).required()
}).required();

export async function validateCaseData(data, options = {}) {
    const defaultOptions = {
        abortEarly: false,
        stripUnknown: true,
        strict: true,
        ...options
    };

    try {
        const validatedData = await caseSchema.validate(data, defaultOptions);

        return {
            isValid: true,
            data: validatedData,
            errors: []
        };
    } catch (error) {
        return {
            isValid: false,
            data: null,
            errors: error.errors || [error.message]
        };
    }
}

export function validateCaseDataSync(data, options = {}) {
    const defaultOptions = {
        abortEarly: false,
        stripUnknown: true,
        strict: true,
        ...options
    };

    try {
        const validatedData = caseSchema.validateSync(data, defaultOptions);

        return {
            isValid: true,
            data: validatedData,
            errors: []
        };
    } catch (error) {
        return {
            isValid: false,
            data: null,
            errors: error.errors || [error.message]
        };
    }
}

const partialCaseSchema = caseSchema.partial();

export async function validatePartialCaseData(data, options = {}) {
    const defaultOptions = {
        abortEarly: false,
        stripUnknown: true,
        strict: false,
        ...options
    };

    try {
        const validatedData = await partialCaseSchema.validate(data, defaultOptions);

        return {
            isValid: true,
            data: validatedData,
            errors: []
        };
    } catch (error) {
        return {
            isValid: false,
            data: null,
            errors: error.errors || [error.message]
        };
    }
}

const defaultDefendant = {
    defendantId: '',
    personalInfo: {
        name: DefaultMultiPlaintiffName.DEFAULT_DEFENDANT_Name
    },
    insurance: {
        insuranceName: '',
        claimNumber: '',
        adjuster: {
            name: '',
            phoneNumber: '',
            email: '',
            address: '',
            city: '',
            state: '',
            zip: ''
        }
    }
};

export function getDefaultDefendant() {
    const result = structuredClone(defaultDefendant);
    result.defendantId = generateRandomIds();
    return result;
}

const defaultClient = {
    clientId: '',
    personalInfo: {
        clientName: DefaultMultiPlaintiffName.DEFAULT_PLAINTIFF_Name,
        phoneNumber: '',
        email: '',
        ssn: '',
        gender: '',
        age: 0
    },
    insurance: {
        insuranceName: '',
        claimNumber: '',
        adjuster: {
            name: '',
            phoneNumber: '',
            email: '',
            address: '',
            city: '',
            state: '',
            zip: ''
        }
    },
    medicalProviders: {
        postAccident: {
            medicalRecords: [],
            result: {
                visitDates: [],
                executiveSummary: '',
                medicalRecordsParagraphs: '',
                simplifiedTreatmentParagraphs: '',
                textExtractions: [],
                medicalBillRecords: [],
                medicalRecordsExhibitDirectoryPath: [],
                medicalRecords: []
            }
        },
        preAccident: {
            medicalRecords: [],
            result: {
                visitDates: [],
                medicalRecordsParagraphs: '',
                medicalRecordsExhibitDirectoryPath: [],
                textExtractions: [],
                medicalRecords: []
            }
        }
    },
    damage: {
        workHoursMissed: 0,
        hourlyIncomeRate: 0,
        typeofWork: '',
        lossOfEarning: [],
        futureMedicalExpense: []
    },
    painAndSuffering: {
        monthlyamount: '',
        annualamount: '',
        descriptionText: '',
        bodilyInjuriesImageFiles: [],
        selectedPainPoints: [],
        result: {}
    }
};

export function getDefaultClient() {
    const result = structuredClone(defaultClient);
    result.clientId = generateRandomIds();
    return result;
}

const defaultCaseInfo = {
    caseName: '',
    caseType: CASE_TYPE.AUTO_ACCIDENT,
    dateOfIncident: '',
    stateOfIncident: '',
    isIncludeExhibit: false,
    isSingleLiabilitySectionForAllDefendants: false
}

export function getDefaultCaseInfo() {
    return defaultCaseInfo;
}

export const defaultMedicalProviders = {
    postAccident: {
        medicalRecords: []
    },
    preAccident: {
        medicalRecords: []
    }
};

export const defaultDamage = {
    workHoursMissed: 0,
    hourlyIncomeRate: 0,
    typeofWork: '',
    lossOfEarning: [],
    futureMedicalExpense: []
};

export const defaultPainAndSuffering = {
    monthlyamount: '',
    annualamount: '',
    descriptionText: '',
    bodilyInjuriesImageFiles: [],
    selectedPainPoints: [],
    result: {}
};